import React, { useState, useMemo, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import useCreateBookingItem from '../../../functions/useCreateBookingItem';
import useCreateNewJob from '../../../functions/useCreateNewJob';
import useUserDropdowns from '../../../functions/useUserDropdowns';
import useJobNumberLogic from '../../../functions/useJobNumberLogic';
import { formatDateForForm } from '../../../utils/dateUtils';
import SaveBanner from '../../JobDetailPage/Mechanical/SaveBanner';

const NewBookingModal = ({ show, handleClose, onBookingCreated }) => {
  const { createBookingItem } = useCreateBookingItem();
  const { createNewJob } = useCreateNewJob();
  const { salesOptions, appsOptions } = useUserDropdowns();
  const { options } = useSelector((state) => state.dropdown);
  const { 
    getNextJobNumberWithFreshData,
    validateJobNumberWithFreshData
  } = useJobNumberLogic();

  // Create combined sales rep options (same as BookingsLog.js)
  const combinedSalesOptions = useMemo(() => {
    const bookingRepsOptions = 
      options?.filter(option => option.DropdownType === "Booking Reps")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
      
    const additionalSalesOptions =
      options?.filter(option => option.DropdownType === "Booking Additional Sales")
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];

    // Create separate arrays for each group
    const salesRepsGroup = [
      ...salesOptions,  // Active directory sales reps
      ...bookingRepsOptions.map(opt => ({
        displayName: opt.DisplayText,
        email: opt.DisplayText
      }))
    ];
    
    const additionalGroup = additionalSalesOptions.map(opt => ({
      displayName: opt.DisplayText,
      email: opt.DisplayText
    }));
    
    // Remove duplicates within each group and sort alphabetically
    const uniqueSalesReps = salesRepsGroup.filter((option, index, self) => 
      index === self.findIndex(opt => opt.displayName === option.displayName)
    );
    
    const uniqueAdditional = additionalGroup.filter((option, index, self) => 
      index === self.findIndex(opt => opt.displayName === option.displayName)
    );
    
    // Combine and sort alphabetically by displayName
    const combined = [...uniqueSalesReps, ...uniqueAdditional];
    return combined.sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [salesOptions, options]);

  // Create Market options (same as BookingsLog.js)
  const marketOptions = useMemo(() => {
    return options?.filter(option => option.DropdownType === 'Market')
      .sort((a, b) => a.SortOrder - b.SortOrder) || [];
  }, [options]);

  // State for new booking data
  const [newBooking, setNewBooking] = useState({
    JobNumber: '',
    ProjectName: '',
    SalesRep: '',
    ProjectManager: '',
    RequestedDeliveryDate: null,
    OrderDate: new Date().toISOString().split('T')[0],
    State: '',
    // Required boolean fields with default false values
    customControlsEngineering: 'false',
    hasSubmittal: 'false',
    newConstruction: 'false',
    sendDocumentsToSales: 'false',
    // PriceBreakdown fields
    Market: '',
    SellPriceAmount: '',
    // SoldToShipTo fields
    SoldToCompanyName: '',
    // New booking type field
    BookingType: '',
  });

  // State for API status tracking - dynamic based on booking type
  const [apiStatus, setApiStatus] = useState({
    generalProject: "idle",
    priceBreakdown: "idle",
    soldToShipTo: "idle",
    bookingJobDetail: "idle",
  });

  // Get API status keys based on booking type
  const getApiStatusKeys = () => {
    if (newBooking.SalesRep !== "International Ag" && newBooking.BookingType === "Booking & Tracker") {
      // APIs used by createNewJob
      return [
        'generalProject', 'generalStation', 'soldToShipTo', 'priceBreakdown', 'ownersManual',
        'hcRequirements', 'skidConstruction', 'pumpManifoldPiping', 'stationEnclosure',
        'dropPipeComments', 'levelControls', 'pumps', 'fertigation', 'skyHarvester',
        'shipLoose', 'waterTreatment', 'inletScreen', 'inletStrainer', 'dischargeStrainer',
        'dischargeFilter', 'roSystem', 'uvSystem', 'unregulatedPower', 'regulatedPowerSupply',
        'sequenceOfOperations', 'electricalOptions', 'telemetry', 'unregulatedBreakers',
        'regulatedBreakers', 'telemetryAccessories'
      ];
    } else {
      // APIs used by createBookingItem
      return ['generalProject', 'priceBreakdown', 'soldToShipTo', 'bookingJobDetail'];
    }
  };

  // State for save banner
  const [showSaveBanner, setShowSaveBanner] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");

  // State for save operation
  const [isSaving, setIsSaving] = useState(false);

  // State for confirmation modal
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [changesMade, setChangesMade] = useState(false);

  // State for job number suggestion and validation
  const [suggestedJobNumber, setSuggestedJobNumber] = useState('');
  const [jobNumberValidation, setJobNumberValidation] = useState({ 
    isValid: true, 
    errors: [], 
    warnings: [] 
  });
  const [loadingJobNumber, setLoadingJobNumber] = useState(false);

  const currentYear = new Date().getFullYear();

  // Fetch suggested job number when modal opens
  useEffect(() => {
    if (show) {
      setLoadingJobNumber(true);
      getNextJobNumberWithFreshData(currentYear)
        .then((suggestedNumber) => {
          setSuggestedJobNumber(suggestedNumber);
          // Set the suggested number as the initial value
          setNewBooking(prev => ({
            ...prev,
            JobNumber: suggestedNumber
          }));
          setLoadingJobNumber(false);
        })
        .catch((error) => {
          console.error('Failed to fetch suggested job number:', error);
          setLoadingJobNumber(false);
        });
    }
  }, [show, currentYear, getNextJobNumberWithFreshData]);

  // Validate job number when it changes
  useEffect(() => {
    if (newBooking.JobNumber && !loadingJobNumber) {
      validateJobNumberWithFreshData(newBooking.JobNumber)
        .then((validationResult) => {
          setJobNumberValidation(validationResult);
        })
        .catch((error) => {
          console.error('Failed to validate job number:', error);
          setJobNumberValidation({ 
            isValid: false, 
            errors: ['Failed to validate job number'], 
            warnings: [] 
          });
        });
    } else if (!newBooking.JobNumber) {
      setJobNumberValidation({ isValid: true, errors: [], warnings: [] });
    }
  }, [newBooking.JobNumber, loadingJobNumber, validateJobNumberWithFreshData]);

  // Validation logic for save button
  const isSaveDisabled = () => {
    // If saving is in progress, disable the button
    if (isSaving) return true;
    
    // If job number validation failed, disable the button
    if (!jobNumberValidation.isValid) return true;
    
    // If no Sales Rep selected, disable the button
    if (!newBooking.SalesRep) return true;
    
    // If Sales Rep is "International Ag", no additional validation needed
    if (newBooking.SalesRep === "International Ag") return false;
    
    // If Sales Rep is not "International Ag", require BookingType selection
    if (newBooking.SalesRep !== "International Ag") {
      return !newBooking.BookingType; // Disabled if no BookingType selected
    }
    
    return false;
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle date fields specifically to allow null values
    if (name === 'OrderDate' || name === 'RequestedDeliveryDate') {
      setNewBooking(prev => ({
        ...prev,
        [name]: value === '' ? null : value
      }));
    } else if (name === 'SellPriceAmount') {
      // Handle currency field - remove formatting and store raw number
      const rawValue = value.replace(/[$,]/g, '');
      setNewBooking(prev => ({
        ...prev,
        [name]: rawValue
      }));
    } else {
      setNewBooking(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    setChangesMade(true);
  };

  // Handle save
  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveStatus("Creating booking...");
      setShowSaveBanner(true);

      let result;

      // Check if Sales Rep is not "International Ag" and Booking Type is "Booking and Tracker"
      if (newBooking.SalesRep !== "International Ag" && newBooking.BookingType === "Booking & Tracker") {
        // Use createNewJob hook - format data with camelCase keys and exclude BookingType
        const jobData = {
          jobNumber: newBooking.JobNumber,
          projectName: newBooking.ProjectName,
          salesRep: newBooking.SalesRep,
          projectManager: newBooking.ProjectManager,
          requestedDeliveryDate: newBooking.RequestedDeliveryDate,
          orderDate: newBooking.OrderDate,
          state: newBooking.State,
          // Required boolean fields
          customControlsEngineering: parseInt(newBooking.customControlsEngineering) || 0,
          hasSubmittal: parseInt(newBooking.hasSubmittal) || 0,
          newConstruction: parseInt(newBooking.newConstruction) || 0,
          sendDocumentsToSales: parseInt(newBooking.sendDocumentsToSales) || 0,
          // Additional fields that might be needed
          bookingLogOnly: 0, // This should be 0 for full jobs, 1 for booking only
          // Note: BookingType is excluded as it's not an API field
        };
        
        // Prepare additional data for other APIs
        const additionalData = {
          // SoldToShipTo fields
          soldToCompanyName: newBooking.SoldToCompanyName,
          soldToState: newBooking.State,
          // PriceBreakdown fields
          market: newBooking.Market,
          sellPriceAmount: newBooking.SellPriceAmount ? 
            parseFloat(newBooking.SellPriceAmount.toString().replace(/[$,]/g, '')) || 0 : 0,
        };
        
        result = await createNewJob(jobData, setApiStatus, additionalData);
      } else {
        // Use createBookingItem hook (default behavior)
        // If Sales Rep is not "International Ag" and Booking Type is "Booking & Schedule", add JobStatus
        let bookingData = { ...newBooking };
        if (newBooking.SalesRep !== "International Ag" && newBooking.BookingType === "Booking & Schedule") {
          bookingData.JobStatus = "Ready to Schedule";
        }
        // Remove BookingType as it's not an API field
        delete bookingData.BookingType;
        result = await createBookingItem(bookingData, setApiStatus);
      }
      
      setSaveStatus("Booking created successfully!");
      
      // Reset form
      setNewBooking({
        JobNumber: '',
        ProjectName: '',
        SalesRep: '',
        ProjectManager: '',
        RequestedDeliveryDate: null,
        OrderDate: new Date().toISOString().split('T')[0],
        State: '',
        // Required boolean fields with default false values
        customControlsEngineering: 'false',
        hasSubmittal: 'false',
        newConstruction: 'false',
        sendDocumentsToSales: 'false',
        // PriceBreakdown fields
        Market: '',
        SellPriceAmount: '',
        // SoldToShipTo fields
        SoldToCompanyName: '',
        // New booking type field
        BookingType: '',
      });
      
      setChangesMade(false);

      // Notify parent component
      if (onBookingCreated) {
        onBookingCreated(result);
      }

      setTimeout(() => {
        setShowSaveBanner(false);
        setIsSaving(false);
        handleClose();
      }, 2000);

    } catch (error) {
      console.error('Error creating booking:', error);
      setSaveStatus("Error creating booking: " + (error.message || "Unknown error"));
      setIsSaving(false);
      setTimeout(() => {
        setShowSaveBanner(false);
      }, 5000);
    }
  };

  // Handle close with confirmation
  const handleCloseWithConfirmation = () => {
    // Don't allow closing while saving
    if (isSaving) return;
    
    if (changesMade) {
      setShowConfirmationModal(true);
    } else {
      handleClose();
    }
  };

  // Handle confirmed close
  const handleConfirmClose = () => {
    setShowConfirmationModal(false);
    setChangesMade(false);
    setIsSaving(false);
    setNewBooking({
      JobNumber: '',
      ProjectName: '',
      SalesRep: '',
      ProjectManager: '',
      RequestedDeliveryDate: null,
      OrderDate: new Date().toISOString().split('T')[0],
      State: '',
      // Required boolean fields with default false values
      customControlsEngineering: 'false',
      hasSubmittal: 'false',
      newConstruction: 'false',
      sendDocumentsToSales: 'false',
      // PriceBreakdown fields
      Market: '',
      SellPriceAmount: '',
      // SoldToShipTo fields
      SoldToCompanyName: '',
      // New booking type field
      BookingType: '',
    });
    handleClose();
  };

  // Render field helper
  const renderField = (field, placeholder = '') => (
    <Form.Group className="mb-3">
      <Form.Label>{field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</Form.Label>
      <Form.Control
        type="text"
        name={field}
        value={newBooking[field] || ''}
        onChange={handleInputChange}
        placeholder={placeholder}
      />
    </Form.Group>
  );

  // Render dropdown field helper
  const renderDropdownField = (field, dropdownOptions, displayNameField = null) => (
    <Form.Group className="mb-3">
      <Form.Label>{field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</Form.Label>
      <Form.Select
        name={field}
        value={newBooking[field] || ''}
        onChange={handleInputChange}
      >
        <option value="">Select...</option>
        {dropdownOptions.map((option) => (
          <option
            key={displayNameField ? option[displayNameField] : option.DropdownValue}
            value={displayNameField ? option[displayNameField] : option.DropdownValue}
          >
            {displayNameField ? option[displayNameField] : option.DropdownValue}
          </option>
        ))}
      </Form.Select>
    </Form.Group>
  );

  return (
    <>
      {showSaveBanner && <SaveBanner showNotification={showSaveBanner} saveStatus={saveStatus} />}
      
      <Modal 
        show={show} 
        onHide={handleCloseWithConfirmation} 
        size="lg"
        dialogClassName="apps-responsive-modal"
        backdrop={isSaving ? 'static' : true}
        keyboard={!isSaving}
      >
        <Modal.Header closeButton={!isSaving}>
          <Modal.Title>Create New Booking Item</Modal.Title>
        </Modal.Header>
        
        <Modal.Body>
          <Form>
            <h5 className="mb-3 border-bottom pb-2">Basic Information</h5>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <strong>Job Number</strong>
                    {suggestedJobNumber && newBooking.JobNumber !== suggestedJobNumber && (
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0 ms-2"
                        onClick={() => {
                          setNewBooking(prev => ({
                            ...prev,
                            JobNumber: suggestedJobNumber
                          }));
                          setChangesMade(true);
                        }}
                        title="Use suggested number"
                      >
                        Use Suggested ({suggestedJobNumber})
                      </Button>
                    )}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="JobNumber"
                    value={newBooking.JobNumber || ''}
                    onChange={handleInputChange}
                    isInvalid={!jobNumberValidation.isValid}
                    placeholder={loadingJobNumber ? 'Loading...' : `Enter job number (e.g., ${suggestedJobNumber})`}
                    disabled={loadingJobNumber}
                  />
                  <Form.Control.Feedback type="invalid">
                    {jobNumberValidation.errors.join(', ')}
                  </Form.Control.Feedback>
                  {jobNumberValidation.warnings.length > 0 && (
                    <div className="text-warning small mt-1">
                      <i className="bi bi-exclamation-triangle me-1"></i>
                      {jobNumberValidation.warnings.join(', ')}
                    </div>
                  )}
                  <Form.Text className="text-muted">
                    Format: YYYY### (e.g., {suggestedJobNumber || '2025001'}). Use -00 for primary, -01, -02, etc. for child numbers.
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>{renderField('ProjectName', 'Enter project name')}</Col>
            </Row>

            <Row>
              <Col md={6}>
                {renderDropdownField('SalesRep', combinedSalesOptions, 'displayName')}
              </Col>
              <Col md={6}>
                {renderDropdownField('ProjectManager', appsOptions, 'displayName')}
              </Col>
            </Row>

            {/* BookingType field - always visible */}
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Booking Type</Form.Label>
                  <Form.Select
                    name="BookingType"
                    value={newBooking.BookingType || ''}
                    onChange={handleInputChange}
                  >
                    <option value="">Select...</option>
                    <option value="Booking Only">Booking Only</option>
                    <option value="Booking & Schedule">Booking & Schedule</option>
                    <option value="Booking & Tracker">Booking & Tracker</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                {/* Empty column for spacing */}
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Order Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="OrderDate"
                    value={newBooking.OrderDate ? formatDateForForm(newBooking.OrderDate) : ''}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Requested Delivery Date</Form.Label>
                  <Form.Control
                    type="date"
                    name="RequestedDeliveryDate"
                    value={newBooking.RequestedDeliveryDate ? formatDateForForm(newBooking.RequestedDeliveryDate) : ''}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <h5 className="mt-4 mb-3 border-bottom pb-2">Pricing Information</h5>
            <Row>
              <Col md={6}>
                {renderDropdownField('Market', marketOptions, 'DisplayText')}
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Sell Price Amount</Form.Label>
                  <Form.Control
                    type="text"
                    name="SellPriceAmount"
                    value={newBooking.SellPriceAmount || ''}
                    onChange={handleInputChange}
                    onBlur={(e) => {
                      // Ensure the value is stored as a clean number when user leaves the field
                      if (e.target.value && !isNaN(parseFloat(e.target.value.replace(/[$,]/g, '')))) {
                        setNewBooking(prev => ({
                          ...prev,
                          SellPriceAmount: e.target.value.replace(/[$,]/g, '')
                        }));
                      }
                    }}
                    placeholder="Enter amount (e.g., 1500.00)"
                  />
                </Form.Group>
              </Col>
            </Row>

            <h5 className="mt-4 mb-3 border-bottom pb-2">Customer Information</h5>
            <Row>
              <Col md={6}>
                {renderField('SoldToCompanyName', 'Enter customer name')}
              </Col>
              <Col md={6}>
                {renderField('State', 'Enter state')}
              </Col>
            </Row>

            {/* API Status Display */}
            {Object.values(apiStatus).some(status => status !== "idle") && (
              <Alert variant="info" className="mt-3">
                <strong>Creation Status:</strong>
                <ul className="mb-0 mt-2">
                  {getApiStatusKeys().map(apiKey => (
                    apiStatus[apiKey] && (
                      <li key={apiKey}>
                        {apiKey.charAt(0).toUpperCase() + apiKey.slice(1).replace(/([A-Z])/g, ' $1')}: {apiStatus[apiKey]}
                      </li>
                    )
                  ))}
                </ul>
              </Alert>
            )}

            {/* Validation message for booking type requirement */}
            {!newBooking.SalesRep && (
              <Alert variant="warning" className="mt-3">
                <strong>Please select a Sales Rep to continue.</strong>
              </Alert>
            )}
            {newBooking.SalesRep && newBooking.SalesRep !== "International Ag" && !newBooking.BookingType && (
              <Alert variant="warning" className="mt-3">
                <strong>Please select a Booking Type to continue.</strong>
              </Alert>
            )}
            {!jobNumberValidation.isValid && (
              <Alert variant="danger" className="mt-3">
                <strong>Job Number Error:</strong> {jobNumberValidation.errors.join(', ')}
              </Alert>
            )}
          </Form>
        </Modal.Body>
        
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseWithConfirmation} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isSaveDisabled()}>
            {isSaving ? 'Creating...' : 'Create Booking'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Confirmation Modal */}
      <Modal show={showConfirmationModal} onHide={() => setShowConfirmationModal(false)} dialogClassName="apps-responsive-modal">
        <Modal.Header closeButton>
          <Modal.Title>Unsaved Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          You have unsaved changes. Are you sure you want to close without creating this booking?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirmationModal(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmClose}>
            Close Without Saving
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

NewBookingModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onBookingCreated: PropTypes.func,
};

export default NewBookingModal;
